import React, { useId } from "react";
import { useDispatch } from "react-redux";
import { useDeepCompareEffect } from "react-use";
import { guardedRequest } from "../../core/guardedRequests.slice";
import { TypedDispatch } from "../../core/store";
import {
  convertPostIdToFaustId,
  creatorsToString,
  flattenCreators,
  getAllFaustIds,
  getManifestationsPids,
  getMaterialTypes,
  getWorkPid
} from "../../core/utils/helpers/general";
import { useText } from "../../core/utils/text";
import { WorkId } from "../../core/utils/types/ids";
import { AvailabilityLabels } from "../availability-label/availability-labels";
import ButtonFavourite, {
  ButtonFavouriteId
} from "../button-favourite/button-favourite";
import { Cover } from "../cover/cover";
import MaterialAvailabilityText from "./MaterialAvailabilityText/MaterialAvailabilityText";
import MaterialHeaderText from "./MaterialHeaderText";
import MaterialButtons from "./material-buttons/MaterialButtons";
import MaterialPeriodical from "./periodical/MaterialPeriodical";
import { Manifestation, Work } from "../../core/utils/types/entities";
import { PeriodicalEdition } from "./periodical/helper";
import { useStatistics } from "../../core/statistics/useStatistics";
import { statistics } from "../../core/statistics/statistics";
import { useItemHasBeenVisible } from "../../core/utils/helpers/lazy-load";
import {
  getManifestationLanguageIsoCode,
  getWorkTitle
} from "../../apps/material/helper";
import { isPeriodical, shouldShowMaterialAvailabilityText } from "./helper";
import useAvailabilityData from "../availability-label/useAvailabilityData";
import { AccessTypeCodeEnum } from "../../core/dbc-gateway/generated/graphql";

interface MaterialHeaderProps {
  wid: WorkId;
  work: Work;
  selectedManifestations: Manifestation[];
  setSelectedManifestations: (manifestations: Manifestation[]) => void;
  selectedPeriodical: PeriodicalEdition | null;
  selectPeriodicalHandler: (selectedPeriodical: PeriodicalEdition) => void;
  children: React.ReactNode;
  isGlobalMaterial: boolean;
}

const MaterialHeader: React.FC<MaterialHeaderProps> = ({
  work: {
    titles: { full: fullTitle, standard: standard },
    creators,
    manifestations: { all: manifestations, bestRepresentation },
    mainLanguages,
    workId: wid
  },
  work,
  selectedManifestations,
  setSelectedManifestations,
  selectedPeriodical,
  selectPeriodicalHandler,
  children,
  isGlobalMaterial = false
}) => {
  const materialTitleId = useId();
  const { itemRef, hasBeenVisible: showItem } = useItemHasBeenVisible();
  const t = useText();
  const dispatch = useDispatch<TypedDispatch>();
  const addToListRequest = (id: ButtonFavouriteId) => {
    dispatch(
      guardedRequest({
        type: "addFavorite",
        args: { id },
        app: "material"
      })
    );
  };
  const author = creatorsToString(flattenCreators(creators), t);
  // const title = getWorkTitle(work);
  const containsDanish = mainLanguages.some((language) =>
    language?.isoCode.toLowerCase().includes("dan")
  );
  const allLanguages = mainLanguages
    .map((language) => language.display)
    .join(", ");
  const allLanguagesParens = allLanguages !== "" ? `${fullTitle} (${allLanguages})` : `${fullTitle}`;
  const title = containsDanish ? standard : allLanguagesParens;
  // const title = containsDanish ? fullTitle : allLanguagesParens;
  // const title = containsDanish ? fullTitle : `${fullTitle} (${allLanguages})`;
  const pid = getWorkPid(work);
  const coverPids = getManifestationsPids(selectedManifestations);
  const { track } = useStatistics();
  // This is used to track whether the user is changing between material types or just clicking the same button over
  const manifestationMaterialTypes = getMaterialTypes(selectedManifestations);

  const languageIsoCode = getManifestationLanguageIsoCode(
    selectedManifestations
  );
  // We need availability in order to show availability text under action buttons
  const { isAvailable } = useAvailabilityData({
    accessTypes: [AccessTypeCodeEnum.Physical, AccessTypeCodeEnum.Online],
    access: [undefined],
    faustIds: getAllFaustIds(selectedManifestations),
    isbn: null, // Not needed.
    // "manifestText" is used inside the availability hook to check whether the material is an article
    // which we check inside shouldShowMaterialAvailabilityText() helper here.
    manifestText: "NOT AN ARTICLE"
  });

  const browserBack = () => {
    window.history.back();
  }

  useDeepCompareEffect(() => {
    track("click", {
      id: statistics.materialType.id,
      name: statistics.materialType.name,
      trackedData: manifestationMaterialTypes.join(", ")
    });
    track("click", {
      id: statistics.materialSource.id,
      name: statistics.materialSource.name,
      trackedData: selectedManifestations
        .map((manifestation) => manifestation.source.join(", "))
        .join(", ")
    });
    // We just want to track if the currently selected manifestation changes (which should be once - on initial render)
    // and when the currently selected manifestation's material type changes - on availability button click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestationMaterialTypes]);

  return (
    <header className="material-header">
      <div className="material-header__cover">
        <Cover
          ids={coverPids}
          bestRepresentation={bestRepresentation}
          size="xlarge"
          animate
          shadow="small"
        />
      </div>
      <div
        data-cy="material-header-content"
        className="material-header__content"
      >
      <div
        onClick={browserBack}
      >
        <div className="advanced-search__backarrow flexing cursor-pointer">
          <svg width="50" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path className="arrow__body" d="M30 8H0" stroke="black" strokeWidth="2"></path>
            <path 
              className="arrow__head" 
              d="M0.293 7.2929C-0.098 7.6834 -0.098 8.3166 0.293 8.7071L6.657 15.0711C7.047 15.4616 7.681 15.4616 8.071 15.0711C8.462 14.6805 8.462 14.0474 8.071 13.6569L2.414 8.00001L8.071 2.3431C8.462 1.9526 8.462 1.3195 8.071 0.928976C7.681 0.538451 7.047 0.538451 6.657 0.928976L0.293 7.2929ZM8 7.00001H0V9.00001H8V7.00001Z"
              fill="black">
            </path>
          </svg>
          <p className="advanced-search__backarrow-text">Gå tilbage</p>
        </div>
      </div>
        <ButtonFavourite
          title={String(title)}
          id={wid}
          addToListRequest={addToListRequest}
        />
        <MaterialHeaderText
          title={(standard && standard.replace(/\s*\(.*?\)$/, "")) || fullTitle[0]}
          // title={String(title)}
          author={author}
          languageIsoCode={languageIsoCode}
          materialTitleId={materialTitleId}
        />
        <div ref={itemRef} className="material-header__availability-label">
          {!isGlobalMaterial && showItem && (
            <AvailabilityLabels
              cursorPointer
              workId={wid}
              manifestations={manifestations}
              selectedManifestations={selectedManifestations}
              setSelectedManifestations={setSelectedManifestations}
            />
          )}
        </div>
        {/* The CTA buttons apparently only make sense on a global work */}
        {!isGlobalMaterial && showItem && (
          <>
            {isPeriodical(selectedManifestations) && (
              <MaterialPeriodical
                faustId={convertPostIdToFaustId(pid)}
                selectedPeriodical={selectedPeriodical}
                selectPeriodicalHandler={selectPeriodicalHandler}
              />
            )}
            {selectedManifestations && (
              <>
                <div className="material-header__button">
                  <MaterialButtons
                    manifestations={selectedManifestations}
                    workId={wid}
                    dataCy="material-header-buttons"
                    materialTitleId={materialTitleId}
                  />
                </div>
                {/* MaterialAvailabilityText is only shown for:
                    - Online manifestations if the user is logged in
                    - physical manifestations
                    - that are not periodical or articles
                    - that are available in at least one local library branch
                */}
                {shouldShowMaterialAvailabilityText(selectedManifestations) &&
                  isAvailable && (
                    <MaterialAvailabilityText
                      manifestations={selectedManifestations}
                    />
                  )}
              </>
            )}
            {children}
          </>
        )}
      </div>
    </header>
  );
};

export default MaterialHeader;
